import { useEffect, useState } from "react"
import type { Country, City, Tag } from "../../shared/types"
import { HeaderImage } from "../../components/HeaderImage"
import { getArticleReadingTime, getImageFromStrapiUploads, getLanguageNameFromLocale, formatDate, getPostTypology, getPostTypologyhref } from "../../shared/utils"
import { GET_AUTHOR, GET_POSTS, addLikeToPost, removeLikeFromPost } from "../../assets/api/apiCalls"
import { useRouter } from "next/router"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { GetStaticPaths, GetStaticProps } from "next"
import { useTranslation } from "next-i18next"
import Link from "next/link"
import { parseCookies, setCookie } from "nookies"
import React from "react"
import Head from 'next/head'
import { getApolloClient } from "../_app"
import { customBlockRenderer } from "../../shared/customBlockRenderer"
import Image from "next/image"
import dynamic from "next/dynamic"
import { Icon } from "@iconify/react"
import { pageTitle } from "../../components/misc/titles"
import { SocialLinks } from "../../components/misc/SocialLinks"

const ShareModal = dynamic(() => import("../../components/ShareModal"), { ssr: false })
const TableOfContent = dynamic(() => import("../../components/TableOfContent"), { ssr: false })
const CommentSection = dynamic(() => import("../../components/CommentSection"), { ssr: false })

export default function Post({ postData: initialPostData, authorData: initialAuthorData }: { postData: any, authorData: any}) {
    const [postData, setPostData] = useState<any>(initialPostData)
    const [authorData, setAuthorData] = useState<any>(initialAuthorData)
    const [postLoading, setPostLoading] = useState<boolean>(true)
    const router = useRouter()
    const [liked, setLiked] = useState<boolean>(false)
    const [showShareModal, setShowShareModal] = useState<boolean>(false)
    const [showAllTags, setShowAllTags] = useState<boolean>(false)
    const slug = router.query.slug as string
    const locale = router.locale
    const [currentLocale, setCurrentLocale] = useState<string>(locale)
    const [languageNotAvailable, setLanguageNotAvailable] = useState<boolean>(false)
    const client = getApolloClient()
    const { t } = useTranslation()

    useEffect(() => {
        if (postData === initialPostData && postData === null) {
            router.push('/post/not_found')
        } else {
            setPostLoading(false)
        }
    }, [])

    const fetchPostData = async (slug: string) => {
        try {
            const { data } = await client.query({
                query: GET_POSTS,
                variables: {
                    "locale": "all",
                    "slug": slug,
                }
            })

            setPostData(data)
            setPostLoading(false)
        } catch (error) {
            console.error('Error fetching posts:', error)
            setPostData(null)
        }
    }

    const fetchAuthorData = async (name: string, surname: string) => {
        try {
            const { data } = await client.query({
                query: GET_AUTHOR,
                variables: {
                    "locale": locale,
                    "name": name,
                    "surname": surname
                }
            })

            setAuthorData(data)
        } catch (error) {
            console.error('Error fetching author:', error)
            setAuthorData(null)
        }
    }

    useEffect(() => {
        if (slug !== undefined && postData !== null && locale === currentLocale) {
            fetchPostData(slug)
        }
    }, [slug])

    useEffect(() => {
        if (!postLoading && postData?.posts?.data?.length === 0) {
            router.push('/post/not_found')
        }
    }, [postLoading])

    useEffect(() => {
        if (!postLoading && postData?.posts?.data?.length === 0) {
            router.push('/post/not_found')
        } else {
            if (postData?.posts?.data.length > 0 && locale !== currentLocale) {
                const localizedSlug = getLocalizedSlug(postData, locale)
                if (localizedSlug !== undefined && localizedSlug.toLowerCase() !== slug) {
                    setCurrentLocale(locale)
                    fetchPostData(localizedSlug)
                    fetchAuthorData(postData?.posts?.data?.[0]?.attributes?.authorName, postData?.posts?.data?.[0]?.attributes?.authorSurname)
                    router.push(`/post/${localizedSlug.toLowerCase()}`)
                } else {
                    setLanguageNotAvailable(true)
                }
            } else {
                setLanguageNotAvailable(true)
            }
        }
    }, [locale])

    useEffect(() => {
        if (postData !== undefined) {
            const cookies = parseCookies()
            const likedPostsStr = cookies.likedPosts
            const likedPosts = likedPostsStr ? JSON.parse(likedPostsStr) : []
            if (likedPosts.includes(postData?.posts?.data?.[0]?.id.toString())) {
                setLiked(true)
            } else {
                setLiked(false)
            }
        }
    }, [postData])

    const checkDifferentPageLanguage = (postData: any, locale: string): boolean => {
        if (languageNotAvailable) {
            if (postData?.posts?.data?.[0]?.attributes?.locale !== locale) {
                return true
            }
        }
        return false
    }

    const getLocalizedSlug = (postData: any, locale: string): string => {
        const localization = postData?.posts?.data?.[0]?.attributes?.localizations?.data.find((item: any) => item.attributes.locale === locale) as { attributes: { slug: string } }
        if (localization === undefined) {
            return undefined
        } else {
            return localization?.attributes?.slug
        }
    }

    const addLike = async (postID: string) => {
        const response = await addLikeToPost({postID}, client)
        if (response?.data?.updatePost?.data?.id === postID) {
            const cookies = parseCookies()
            let likedPostsStr = cookies.likedPosts
            const likedPosts = likedPostsStr ? JSON.parse(likedPostsStr) : []
            likedPosts.push(postID)
            likedPostsStr = JSON.stringify(likedPosts)
            setCookie(null, 'likedPosts', likedPostsStr, { path: '/' })
            setLiked(true)
        }
    }

    const removeLike = async (postID: string) => {
        const response = await removeLikeFromPost({postID}, client)
        if (response?.data?.updatePost?.data?.id === postID) {
            const cookies = parseCookies()
            let likedPostsStr = cookies.likedPosts
            const likedPosts = likedPostsStr ? JSON.parse(likedPostsStr) : []
            const index = likedPosts.indexOf(postID)
            if (index > -1) {
                likedPosts.splice(index, 1)
            }
            likedPostsStr = JSON.stringify(likedPosts)
            setCookie(null, 'likedPosts', likedPostsStr, { path: '/' })
            setLiked(false)
        }
    }

    const getPageInOtherLanguages = (postData: any) => {
        if (!postData?.posts?.data?.[0]?.attributes?.localizations?.data) {
            return null
        }
    
        return postData.posts.data[0].attributes.localizations.data.map(
            (localization: any) => (
                <link 
                    key={localization.attributes.locale} 
                    rel="alternate" 
                    hrefLang={localization.attributes.locale} 
                    href={`https://explorerstale.com/post/${localization.attributes.slug}`} 
                />
            )
        )
    }

    const getCanonicalUrl = (postData: any) => {
        const postLocale = postData?.posts?.data?.[0]?.attributes?.locale
        if (postLocale !== 'it') {
            return `https://explorerstale.com/${postLocale}/post/${postData?.posts?.data?.[0]?.attributes?.slug.toLowerCase()}`
        } else {
            return `https://explorerstale.com/post/${postData?.posts?.data?.[0]?.attributes?.slug.toLowerCase()}`
        }
    }

    const getKeywordTags = (postData: any) => {
        if (postData?.posts?.data?.[0]?.attributes?.seoKeywords) {
            return postData?.posts?.data?.[0]?.attributes?.seoKeywords
        } else {
            return postData?.posts?.data?.[0]?.attributes?.tags?.data.map((tag: Tag) => tag?.attributes?.name).join(", ")
        }    
    }

    const getMetadata = () => {
        return (
            <Head>
                <title>{postData?.posts?.data?.[0]?.attributes?.postTitle}</title>
                <meta name="description" content={postData?.posts?.data?.[0]?.attributes?.seoDescription !== null ? postData?.posts?.data?.[0]?.attributes?.seoDescription : postData?.posts?.data?.[0]?.attributes?.postDescription} key="desc" />
                <meta property="og:title" content={postData?.posts?.data?.[0]?.attributes?.postTitle} />
                <meta property="og:description" content={postData?.posts?.data?.[0]?.attributes?.seoDescription !== null ? postData?.posts?.data?.[0]?.attributes?.seoDescription : postData?.posts?.data?.[0]?.attributes?.postDescription} />
                <meta property="og:image" content={postData?.posts?.data?.[0]?.attributes?.postCover?.data?.attributes?.url} />
                <meta property="og:url" content={getCanonicalUrl(postData)} />
                <meta property="og:type" content="article" />
                <meta property="og:locale" content={currentLocale} />
                <meta property="og:site_name" content="Explorer's Tale" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@explorerstale" />
                <meta name="twitter:creator" content="@explorerstale" />
                <meta name="twitter:title" content={`Explorer's Tale | ${postData?.posts?.data?.[0]?.attributes?.postTitle}`} />
                <meta name="twitter:description" content={postData?.posts?.data?.[0]?.attributes?.seoDescription !== null ? postData?.posts?.data?.[0]?.attributes?.seoDescription : postData?.posts?.data?.[0]?.attributes?.postDescription} />
                <meta name="twitter:image" content={postData?.posts?.data?.[0]?.attributes?.postCover?.data?.attributes?.url} />
                <link rel="canonical" href={getCanonicalUrl(postData)} />
                <meta name="author" content={authorData?.authorsinfo?.data?.[0]?.attributes?.name + " " + authorData?.authorsinfo?.data?.[0]?.attributes?.surname} />
                <meta name="robots" content="all" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="content-language" content={currentLocale} />
                <meta name="keywords" content={getKeywordTags(postData)} />
                {getPageInOtherLanguages(postData)}
                <link rel="alternate" href="https://explorerstale.com" hrefLang="x-default" />
            </Head>
        )
    }

    return (
        <>
            {postData && getMetadata()}
            {postData?.posts?.data?.[0]?.attributes?.postCover?.data && postData?.posts?.data?.[0]?.attributes?.typology != "discount" && (
                <HeaderImage
                    image={postData?.posts?.data?.[0]?.attributes?.postCover?.data !== null ? getImageFromStrapiUploads(postData?.posts?.data?.[0]?.attributes?.postCover?.data?.attributes) : "/assets/no-img.jpg"}
                    alt={postData?.posts?.data?.[0]?.attributes?.slug+`-cover`}
                    country={postData?.posts?.data?.[0]?.attributes?.countries?.data?.[0]?.attributes?.name}
                    title={postData?.posts?.data?.[0]?.attributes?.postTitle}
                />
            )}
            
            {!postData ? (
                <div className="bg-white p-4 rounded animate-pulse max-w-[80rem] mx-auto">
                    <div className="h-4 w-full h-[3rem] mb-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-3/4 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-4/6 mt-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-5/6 mt-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[2rem] mb-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-5/6 mt-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-3/4 bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-4/6 mt-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center">
                    {(postData && checkDifferentPageLanguage(postData, locale)) && (
                        <div className="flex flex-col justify-center mx-auto">
                            <div className="flex justify-center mx-auto mt-5 mr-2 ml-2">
                                <div className="flex flex-row bg-quaternary p-2 rounded-xl">
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{t('post.language_not_available', { post_locale: t(`language.${getLanguageNameFromLocale(postData?.posts?.data?.[0]?.attributes?.locale)}`), current_locale: t(`language.${getLanguageNameFromLocale(locale)}`)})}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                        <div className='relative max-w-[80rem] mx-auto mt-5 sm:mt-5 font-poppins'>
                            <div className='flex flex-col mx-auto mr-5 ml-5 justify-center gap-2'>
                                <div className="flex flex-col xs:flex-row gap-1 pt-2 text-sm">
                                    {postData?.posts?.data?.[0]?.attributes?.typology !== "other" ? (
                                        <Link className="text-primary font-bold" href={getPostTypologyhref(postData?.posts?.data?.[0]?.attributes?.typology)}>
                                            {t("post.typology."+getPostTypology(postData?.posts?.data?.[0]?.attributes?.typology))}
                                        </Link>
                                    ) : (
                                        <p>{t("post.typology."+getPostTypology(postData?.posts?.data?.[0]?.attributes?.typology))}</p>
                                    )}
                                    <p className="hidden xs:block">-</p>
                                    <p className="flex flex-row justify-left xs:justify-center items-center gap-1 text-gray-400">
                                        <Icon icon="mdi:glasses" className="h-5 w-5" />
                                        {getArticleReadingTime(postData?.posts?.data?.[0]?.attributes?.postContent)}
                                        <span>min</span>
                                    </p>
                                </div>
                                <div className="flex flex-row gap-5 justify-center">
                                    <div className="flex flex-col w-full justify-start gap-3 mb-3">
                                        {pageTitle({title: postData?.posts?.data?.[0]?.attributes?.postTitle})}
                                        <span className="prose max-w-none">{postData?.posts?.data?.[0]?.attributes?.postDescription}</span>
                                        <TableOfContent postData={postData?.posts?.data?.[0]?.attributes?.postContent} />
                                        {postData && postData?.posts?.data?.[0]?.attributes?.postContent !== undefined && (
                                            customBlockRenderer(postData?.posts?.data?.[0]?.attributes?.postContent, t)
                                        )}
                                    </div>
                                    <div className="float-right hidden xl:block w-[15rem]">
                                        <div className="flex flex-col gap-2">
                                            {authorData && (
                                                <div className='flex flex-col justify-center items-center gap-3 bg-quaternary rounded-xl p-3'>
                                                    <Image
                                                        src={authorData?.authorsinfo?.data?.[0]?.attributes?.picture?.data !== null ? getImageFromStrapiUploads(authorData?.authorsinfo?.data?.[0]?.attributes?.picture?.data?.attributes, "thumbnail") : "/assets/no-user-img.jpg"}
                                                        alt={authorData?.authorsinfo?.data?.[0]?.attributes?.name}
                                                        className="rounded-full"
                                                        style={{
                                                            objectFit: 'cover',
                                                        }}
                                                        width={110}
                                                        height={110}
                                                    />
                                                    <div className='flex flex-col gap-2'>
                                                        {authorData && authorData?.authorsinfo?.data?.length > 0 && (
                                                            <div className='prose text-center text-sm'>
                                                                {customBlockRenderer(authorData?.authorsinfo?.data?.[0]?.attributes?.shortDescription)}
                                                            </div>
                                                        )}
                                                        <div className="flex flex-row gap-5 text-lg text-center justify-center">
                                                            {SocialLinks(t)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {(postData?.posts?.data?.[0]?.attributes?.countries?.data?.length > 0 || postData?.posts?.data?.[0]?.attributes?.cities?.data?.length > 0) && (
                                                <div className="flex flex-col bg-gray-100 rounded-xl p-3 gap-2">
                                                    <p className="text-sm">{t("post.in_this_article")}:</p>
                                                    <ul role="list" className="space-y-2">
                                                        {(postData?.posts?.data?.[0]?.attributes?.countries?.data !== undefined) && (postData?.posts?.data?.[0]?.attributes?.countries?.data).map((country: Country) => (
                                                            <li className="relative" key={country.id}>
                                                                <Link href={{ pathname: "/country/[name]", query: { name: country.attributes?.name.toLowerCase()} }} className="hover:no-underline">
                                                                    <div className="flex flex-row gap-2 text-left items-center">
                                                                        <div className="relative h-[3rem] w-[4rem]">
                                                                            <Image
                                                                                src={country?.attributes?.countryCover?.data !== null ? getImageFromStrapiUploads(country?.attributes?.countryCover?.data?.attributes, "thumbnail") : "/assets/no-img.jpg"}
                                                                                alt={country?.attributes?.name+`-cover`}
                                                                                className="object-cover rounded-md"
                                                                                fill={true}
                                                                            />
                                                                        </div>
                                                                        <div className="flex flex-col">
                                                                            <p className="text-primary uppercase font-bold">{country?.attributes?.name}</p>
                                                                            <p className="text-gray-600 uppercase text-xs">{t("country.country")}</p>
                                                                        </div>
                                                                    </div>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                        {(postData?.posts?.data?.[0]?.attributes?.cities?.data !== undefined) && (postData?.posts?.data?.[0]?.attributes?.cities?.data).map((city: City) => (
                                                            <li className="relative" key={city.id}>
                                                                <Link href={{ pathname: "/city/[name]", query: { name: city?.attributes?.name.toLowerCase()} }} className="hover:no-underline">
                                                                    <div className="flex flex-row gap-2 text-left items-center">
                                                                        <div className="relative h-[3rem] w-[4rem]">
                                                                            <Image
                                                                                src={city?.attributes?.cityCover?.data !== null ? getImageFromStrapiUploads(city?.attributes?.cityCover?.data?.attributes, "thumbnail") : "/assets/no-img.jpg"}
                                                                                alt={city?.attributes?.name+`-cover`}
                                                                                className="object-cover rounded-md"
                                                                                fill={true}
                                                                            />
                                                                        </div>
                                                                        <div className="flex flex-col">
                                                                            <p className="text-primary uppercase font-bold">{city?.attributes?.name}</p>
                                                                            <p className="text-gray-600 uppercase text-xs">{t("city.city")}</p>
                                                                        </div>
                                                                    </div>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <div className="flex flex-col bg-gray-100 rounded-xl p-3 gap-2">
                                                {liked ? (
                                                    <div className="flex flex-row items-center gap-1 cursor-pointer" onClick={() => removeLike(postData?.posts?.data?.[0]?.id)}>
                                                        <Icon icon="mdi:heart" color="red"/>
                                                        <p className="text-sm">{t("post.unlike")}</p>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-row items-center gap-1 cursor-pointer" onClick={() => addLike(postData?.posts?.data?.[0]?.id)}>
                                                        <Icon icon="mdi:heart-outline" />
                                                        <p className="text-sm">{t("post.like")}</p>
                                                    </div>
                                                )}
                                                <div className="flex flex-row items-center gap-1 cursor-pointer" onClick={() => setShowShareModal(true)}>
                                                    <Icon icon="mdi:share-variant" />
                                                    <p className="text-sm">{t("post.share")}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-left text-sm">{t("post.last_update")}: {formatDate(postData?.posts?.data?.[0]?.attributes?.updatedAt)}</p>
                                <div className="flex flex-row justify-between bg-gray-100 rounded-xl p-2 gap-2">
                                    {liked ? (
                                        <div className="flex flex-row items-center gap-1 cursor-pointer" onClick={() => removeLike(postData?.posts?.data?.[0]?.id)}>
                                            <Icon icon="mdi:heart" color="red" />
                                            <span className="text-sm">{t("post.unlike")}</span>
                                        </div>
                                        ) : (
                                        <div className="flex flex-row items-center gap-1 cursor-pointer" onClick={() => addLike(postData?.posts?.data?.[0]?.id)}>
                                            <div className="like-animation relative">
                                                <Icon icon="mdi:heart-outline" />
                                                <div className="hearts absolute top-[-10px] left-5 animate-flyAway">
                                                    <i className="fas fa-heart text-red text-xs opacity-80 mr-[-5px]"></i>
                                                    <i className="fas fa-heart text-red text-xs opacity-80 mr-[-5px]"></i>
                                                    <i className="fas fa-heart text-red text-xs opacity-80"></i>
                                                </div>
                                            </div>
                                            <span className="text-sm">{t("post.like")}</span>
                                        </div>
                                    )}
                                    <div className="flex flex-row items-center gap-1 cursor-pointer" onClick={() => setShowShareModal(true)}>
                                        <Icon icon="mdi:share-variant" />
                                        <p className="text-sm">{t("post.share")}</p>
                                    </div>
                                </div>
                                {(postData?.posts?.data?.[0]?.attributes?.countries?.data?.length > 0 || postData?.posts?.data?.[0]?.attributes?.cities?.data?.length > 0) && (
                                    <div className="flex flex-col bg-gray-100 rounded-xl p-3 gap-2">
                                        <p className="text-sm">{t("post.in_this_article")}:</p>
                                        <ul role="list" className="space-y-2">
                                            {(postData?.posts?.data?.[0]?.attributes?.countries?.data !== undefined) && (postData?.posts?.data?.[0]?.attributes?.countries?.data).map((country: Country) => (
                                                <li className="relative" key={country?.id}>
                                                    <Link href={{ pathname: "/country/[name]", query: { name: country?.attributes?.name.toLowerCase()} }} className="hover:no-underline">
                                                        <div className="flex flex-row gap-2 text-left items-center">
                                                            <div className="relative h-[3rem] w-[4rem]">
                                                                <Image
                                                                    src={country?.attributes?.countryCover?.data !== null ? getImageFromStrapiUploads(country?.attributes?.countryCover?.data?.attributes, "thumbnail") : "/assets/no-img.jpg"}
                                                                    alt={country?.attributes?.name+`-cover`}
                                                                    className="object-cover rounded-md"
                                                                    fill={true}
                                                                />
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <p className="text-primary uppercase font-bold">{country?.attributes?.name}</p>
                                                                <p className="text-gray-600 uppercase text-xs">{t("country.country")}</p>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </li>
                                            ))}
                                            {(postData?.posts?.data?.[0]?.attributes?.cities?.data !== undefined) && (postData?.posts?.data?.[0]?.attributes?.cities?.data).map((city: City) => (
                                                <li className="relative" key={city?.id}>
                                                    <Link href={{ pathname: "/city/[name]", query: { name: city?.attributes?.name.toLowerCase()} }} className="hover:no-underline">
                                                        <div className="flex flex-row gap-2 text-left items-center">
                                                            <div className="relative h-[3rem] w-[4rem]">
                                                                <Image
                                                                    src={city?.attributes?.cityCover?.data !== null ? getImageFromStrapiUploads(city?.attributes?.cityCover?.data?.attributes, "thumbnail") : "/assets/no-img.jpg"}
                                                                    alt={city?.attributes?.name+`-cover`}
                                                                    className="object-cover rounded-md"
                                                                    fill={true}
                                                                />
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <p className="text-primary uppercase font-bold">{city?.attributes.name}</p>
                                                                <p className="text-gray-600 uppercase text-xs">{t("city.city")}</p>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {postData?.posts?.data?.[0]?.attributes?.tags && postData?.posts?.data?.[0]?.attributes?.tags?.data?.length > 0 && (
                                    <div className="flex flex-col bg-gray-100 rounded-xl p-3 gap-2">
                                        <p className="text-sm">{t("post.tags")}:</p>
                                        <div className="flex flex-wrap gap-2 text-left">
                                            {(postData?.posts?.data?.[0]?.attributes?.tags?.data).slice(0, 5).map((tag: Tag) => (
                                                <Link href={{ pathname: "/tag/[name]", query: { name: tag?.attributes?.name.toLowerCase()} }} className="hover:no-underline" key={tag?.id!}>
                                                    <span className="uppercase text-black text-xs bg-gray-200 rounded-xl p-1">{tag?.attributes?.name}</span>
                                                </Link>
                                            ))}
                                            {showAllTags && (postData?.posts?.data?.[0]?.attributes?.tags?.data).slice(5).map((tag: Tag) => (
                                                <Link href={{ pathname: "/tag/[name]", query: { name: tag?.attributes?.name.toLowerCase()} }} className="hover:no-underline" key={tag?.id!}>
                                                    <span className="uppercase text-black text-xs bg-gray-200 rounded-xl p-1">{tag?.attributes?.name}</span>
                                                </Link>
                                            ))}
                                            {postData?.posts?.data?.[0]?.attributes?.tags?.data?.length > 5 && (
                                            <button onClick={() => {showAllTags ? setShowAllTags(false) : setShowAllTags(true)}} className="uppercase text-black text-xs bg-gray-200 rounded-xl p-1 hover:no-underline">
                                                {showAllTags ? (
                                                    <div className="flex flex-row gap-1 items-center">
                                                        {t("common.show_less")}
                                                        <Icon icon="mdi:chevron-up" />
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-row gap-1 items-center">
                                                        {t("common.show_more")}
                                                        <Icon icon="mdi:chevron-down" />
                                                    </div>
                                                )}
                                            </button>
                                        )}
                                        </div>
                                        
                                    </div>
                                )}
                                {authorData && (
                                    <div className='flex flex-col mx-auto justify-center gap-5'>
                                        <div className='flex flex-col justify-center items-center lg:flex-row lg:justify-start lg:items-start gap-2 bg-quaternary rounded-xl p-3'>
                                                <Image
                                                    src={authorData?.authorsinfo?.data?.[0]?.attributes?.picture?.data !== null ? getImageFromStrapiUploads(authorData?.authorsinfo?.data?.[0]?.attributes?.picture?.data?.attributes, "thumbnail") : "/assets/no-user-img.jpg"}
                                                    alt={authorData?.authorsinfo?.data?.[0]?.attributes?.name}
                                                    className="rounded-full"
                                                    style={{
                                                        objectFit: 'cover',
                                                    }}
                                                    width={110}
                                                    height={110}
                                                />
                                            <div className='flex flex-col gap-3'>
                                                {authorData && authorData?.authorsinfo?.data?.length > 0 && (
                                                    <div className='prose max-w-none text-center lg:text-left'>
                                                        {customBlockRenderer(authorData?.authorsinfo?.data?.[0]?.attributes?.longDescription)}
                                                    </div>
                                                )}
                                                <div className="flex flex-row gap-5 text-lg text-center justify-center lg:text-left lg:justify-start ">
                                                    {SocialLinks(t)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <CommentSection postID={postData?.posts?.data?.[0]?.id} />
                            </div>
                        </div>
                        <ShareModal showModal={showShareModal} setShowModal={setShowShareModal} articleUrl={typeof window !== 'undefined' ? window.location.href : ""} />
                </div>  
            )}
        </>
    )
}

export const getStaticProps: GetStaticProps = async (context) => {
    const client = getApolloClient()
    const locale = context.locale
    const slug = context.params?.slug as string

    let authorData = null;
    
    try {
        let { data: postData } = await client.query({
            query: GET_POSTS,
            variables: {
                "locale": "all",
                "slug": slug,
            }
        })
    
        // Extract the post data or provide a default/fallback
        postData = postData.posts.data.length > 0 ? postData : null
    
        if (postData !== null) {
            let { data } = await client.query({
                query: GET_AUTHOR,
                variables: {
                    "locale": locale,
                    "name": postData?.posts?.data?.[0]?.attributes?.authorName,
                    "surname": postData?.posts?.data?.[0]?.attributes?.authorSurname
                }
            })
            authorData = data
        }
    
        if (authorData !== null) {
            authorData = authorData.authorsinfo.data.length > 0 ? authorData : null
        }
    
        return {
            props: {
                ...(await serverSideTranslations(locale, ['common', 'navbar', 'post'])),
                postData,
                authorData
            }
        }
    } catch (error) {
        console.error('Error fetching posts:', error)
    
        // Return empty props or an error message
        return {
            props: {
                ...(await serverSideTranslations(locale, ['common', 'navbar', 'post'])),
                postData: null,
                authorData: null,
                error: 'Failed to fetch post data',
            },
        }
    }
}

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
    return {
        paths: [],
        fallback: 'blocking'
    }
}
